/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Checkbox } from "@mui/material";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { isBitSet, getAlarmLedColor } from "../utils";

const DeuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const deuAlarms = parsedAttributes.deuAlarms;
  const deuAlarmsMask = parsedAttributes.deuAlarmsMask;

  const deuTopologyLocation = parsedAttributes.deuTopologyLocation;
  const deuEquipmentModel = parsedAttributes.deuEquipmentModel || device.model;
  const deuEquipmentSN = parsedAttributes.deuEquipmentSN || device.serialNumber;
  const deuTemperature = parsedAttributes.deuTemperature;
  const deuFirmwareVersion = parsedAttributes.deuFirmwareversion;
  const deuHardwareVersion = parsedAttributes.deuHardwareversion;
  const deuSfpInfo = parsedAttributes.deuSFPInfo || {};
  const deuFanAlarm = Array.isArray(parsedAttributes.deuFanAlram)
    ? parsedAttributes.deuFanAlram
    : Array.isArray(parsedAttributes.deuFanAlarm)
      ? parsedAttributes.deuFanAlarm
      : [];

  const hasAlarms = deuAlarms !== undefined || deuAlarmsMask !== undefined;
  const hasGeneralInfo =
    deuTopologyLocation ||
    deuEquipmentModel ||
    deuEquipmentSN ||
    deuTemperature !== undefined ||
    deuFirmwareVersion ||
    deuHardwareVersion;
  const sfpPorts: string[] = Array.from(
    new Set(
      Object.keys(deuSfpInfo)
        .map(
          (key) =>
            key.match(/deuSFP(?:Alarms|AlarmsMask|TxPower|RxPower|Temperature)-(.+)$/)?.[1]
        )
        .filter((port): port is string => !!port)
    )
  ).sort((a, b) => {
    const aNum = Number(a);
    const bNum = Number(b);

    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
      return aNum - bNum;
    }

    return a.localeCompare(b);
  });

  const sfpAlarmRows: AlarmDefinition[] = [
    { bit: 1, name: "Cpri Link Rx Alarm" },
    { bit: 2, name: "Cpri Link Tx Alarm" },
    { bit: 3, name: "Cpri Sync Alarm" },
    { bit: 4, name: "Cpri Link Fault Alarm" },
    { bit: 5, name: "Cpri Over Temperature Alarm" },
    { bit: 6, name: "Cpri Manufacturer Invalid Alarm" },
    { bit: 7, name: "Cpri Rate Mismatch Alarm" },
  ];
  const formatSfpValue = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    //if (num <= -99) return "N/A";
    return String(num);
  };

  const renderMetricValueWithUnit = (
    value: unknown,
    unit: string,
    valueClassName = "font-medium"
  ) => {
    const formattedValue = formatSfpValue(value);

    if (formattedValue === "N/A") {
      return <span className="font-medium text-muted-foreground">N/A</span>;
    }

    return (
      <span className={valueClassName}>
        {formattedValue} {unit}
      </span>
    );
  };

  const hasSfpAlarms = sfpPorts.length > 0;
  const deuFanAlarmSorted = [...deuFanAlarm].sort(
    (a: any, b: any) => Number(a?.deuFanNumber) - Number(b?.deuFanNumber)
  );
  const hasFanAlarm = deuFanAlarmSorted.length > 0;

  const isDisconnected = statusLabel === "Disconnected";

  const alarmDefinitions: AlarmDefinition[] = [
    { bit: 0, name: "Overall Alarm" },
    { bit: 1, name: "Temperature Alarm" },
    { bit: 2, name: "Power Temperature Alarm" },
    { bit: 3, name: "Digital HW Alarm" },
    { bit: 4, name: "Digital UnClock Alarm" },
    { bit: 5, name: "Firmware Mismatch Alarm" },
    { bit: 6, name: "DC Voltage Lower Alarm" },
  ];

  if (alarmsOnly) {
    if (!hasAlarms) return null;

    return (
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">Alarms</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <AlarmTable
            alarms={deuAlarms}
            alarmsMask={deuAlarmsMask}
            alarmDefinitions={alarmDefinitions}
            isDisconnected={isDisconnected}
          />
        </CardContent>
      </Card>
    );
  }

  if (!hasGeneralInfo && !hasAlarms && !hasSfpAlarms && !hasFanAlarm) return null;

  return (
    <>
      {hasGeneralInfo && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Device Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {deuTopologyLocation && <DetailRow label="Topology Location" value={deuTopologyLocation} />}
            {deuEquipmentModel && <DetailRow label="Model" value={deuEquipmentModel} />}
            {deuEquipmentSN && <DetailRow label="Serial Number" value={deuEquipmentSN} />}
            {deuTemperature !== undefined && <DetailRow label="Temperature" value={`${deuTemperature}°C`} />}
            {deuFirmwareVersion && <DetailRow label="Firmware Version" value={deuFirmwareVersion} />}
            {deuHardwareVersion && <DetailRow label="Hardware Version" value={deuHardwareVersion} />}
          </CardContent>
        </Card>
      )}

      {hasAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={deuAlarms}
              alarmsMask={deuAlarmsMask}
              alarmDefinitions={alarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {hasSfpAlarms && (
        <Card className="shadow-sm border">
          <CardHeader className="px-4 py-3 border-b">
            <CardTitle className="text-sm font-semibold tracking-tight">
              SFP Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {sfpPorts.map((port) => {
                const alarmsVal = deuSfpInfo[`deuSFPAlarms-${port}`];
                const maskVal = deuSfpInfo[`deuSFPAlarmsMask-${port}`];

                return (
                  <div
                    key={port}
                    className="rounded-xl border bg-card p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-semibold">
                        Port {port}
                      </div>

                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: getAlarmLedColor(
                            isDisconnected,
                            Number(alarmsVal) > 0
                          ),
                        }}
                      />
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          Temperature
                        </span>

                        {renderMetricValueWithUnit(
                          deuSfpInfo[`deuSFPTemperature-${port}`],
                          "°C"
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          TX Power
                        </span>

                        {renderMetricValueWithUnit(
                          deuSfpInfo[`deuSFPTxPower-${port}`],
                          "dBm"
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          RX Power
                        </span>

                        {renderMetricValueWithUnit(
                          deuSfpInfo[`deuSFPRxPower-${port}`],
                          "dBm"
                        )}
                      </div>
                    </div>

                    <div className="mt-4 border-t pt-3 space-y-2">
                      {sfpAlarmRows.map((alarm) => {
                        const isAlarmActive = isBitSet(
                          alarmsVal,
                          alarm.bit
                        );

                        const isMaskEnabled = isBitSet(
                          maskVal,
                          alarm.bit
                        );

                        return (
                          <div
                            key={alarm.bit}
                            className="flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor:
                                    getAlarmLedColor(
                                      isDisconnected,
                                      isAlarmActive
                                    ),
                                }}
                              />

                              <span className="text-[11px] truncate text-muted-foreground">
                                {alarm.name}
                              </span>
                            </div>

                            <Checkbox
                              checked={isMaskEnabled}
                              disabled
                              size="small"
                              sx={{
                                padding: 0,
                                transform: "scale(0.7)",
                                color:
                                  "var(--foreground)",
                                "&.Mui-disabled": {
                                  color:
                                    "var(--muted-foreground)",
                                },
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {hasFanAlarm && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Fan Alarm</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {deuFanAlarmSorted.map((fan: any, index: number) => {
                const fanNumber = fan?.deuFanNumber ?? index + 1;
                const isAlarmActive = Number(fan?.deuFanAlarm) > 0;
                const isMaskEnabled = Number(fan?.deuFanAlarmMask) > 0;

                return (
                  <div
                    key={`${fan?.deuNumber ?? "1"}-${fanNumber}-${index}`}
                    className="rounded-xl border p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">
                        Fan {fanNumber}
                      </div>

                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive),
                        }}
                      />
                    </div>

                    <div className="mt-3 text-xs flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">
                        Status:{" "}
                        <span className="font-medium text-foreground">
                          {isAlarmActive ? "Alarm Active" : "Normal"}
                        </span>
                      </span>
                      <Checkbox
                        checked={isMaskEnabled}
                        disabled
                        size="small"
                        sx={{
                          padding: 0,
                          transform: "scale(0.7)",
                          color: "var(--foreground)",
                          "&.Mui-disabled": {
                            color: "var(--muted-foreground)",
                          },
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default DeuDeviceDetails;